import { useCallback, useEffect, useRef } from "react";
import { useDispatch, useStore } from "react-redux";
import type { AppDispatch } from "@/features/redux/store";
import { useGlobalState } from "@/store/commonStore";
import { useWsUpdateBanblce } from "@/store/hallSocket";
import { detectClientEnvironment } from "@/features/clientEnv";
import { getCurrentInstallLink } from "@/utils/countryConfigUtils";
import { enqueue } from "./modalOrchestratorSlice";
import {
  isClosableDownloadGatePopType,
  pickHighestPriorityDownloadGateItem,
} from "./downloadGate";
import {
  fetchPopupActivityList,
  getPopupActivityRequestScope,
} from "./popupActivityList";
import type { IFActivityDetail } from "@/api/apiRsp/IFActivityList";

export const usePageEntryDownloadGate = () => {
  const dispatch = useDispatch<AppDispatch>();
  const store = useStore();
  const { isLogin, token } = useGlobalState();
  const { data: wsData } = useWsUpdateBanblce();
  const checkedRef = useRef<string | null>(null);
  const aliveRef = useRef(true);
  const lastProcessedRechargeWsDataRef = useRef<any>(null);
  const authRef = useRef({ isLogin, token });
  authRef.current = { isLogin, token };

  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);

  const checkDownloadGate = useCallback(async () => {
    if (
      !isLogin ||
      !token ||
      !authRef.current.isLogin ||
      authRef.current.token !== token
    )
      return;

    const env = detectClientEnvironment();
    if (!env.isFacebookFamilyIAB) return;

    const installLink = getCurrentInstallLink();
    if (!installLink) return;

    const requestScope = getPopupActivityRequestScope(store, token);
    let activityRes: IFActivityDetail[];
    try {
      activityRes = await fetchPopupActivityList(requestScope);
    } catch {
      return;
    }

    if (
      !aliveRef.current ||
      !authRef.current.isLogin ||
      authRef.current.token !== token ||
      getPopupActivityRequestScope(store, token) !== requestScope
    )
      return;

    const item = pickHighestPriorityDownloadGateItem(activityRes);
    if (!item) return;

    dispatch(
      enqueue({
        id: `download_gate_${item.id}`,
        type: "download_gate",
        priority: 45,
        dedupeKey: "download_gate",
        payload: {
          closable: isClosableDownloadGatePopType(item.popType),
          installLink,
        },
      })
    );
  }, [dispatch, store, isLogin, token]);

  useEffect(() => {
    if (!isLogin) {
      checkedRef.current = null;
      getPopupActivityRequestScope(store, null);
      lastProcessedRechargeWsDataRef.current = null;
      return;
    }

    if (checkedRef.current === token) return;

    checkedRef.current = token;
    void checkDownloadGate();
  }, [checkDownloadGate, store, isLogin, token]);

  useEffect(() => {
    if (!isLogin || !wsData?.tradeModeCode?.includes("RECHARGE")) {
      return;
    }
    if (lastProcessedRechargeWsDataRef.current === wsData) {
      return;
    }
    lastProcessedRechargeWsDataRef.current = wsData;

    const timer = window.setTimeout(() => {
      void checkDownloadGate();
    }, 500);

    return () => {
      window.clearTimeout(timer);
    };
  }, [checkDownloadGate, isLogin, wsData]);
};

export default usePageEntryDownloadGate;
